import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Phone, Upload } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { routeSlug } from "@/i18n/routes";
import { useFunnel } from "@/lib/funnel";
import Seo from "@/components/Seo";
import AskVytali from "@/components/AskVytali";
import MobileQuickActions from "@/components/MobileQuickActions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/recruit`;
const PHONE_HREF = "tel:+14185496833";
const PHONE_LABEL = "+1 418-549-6833";
const SALES_LEVELS = ["none", "beginner", "intermediate", "experienced"];
const AVAILABILITY = ["full_time", "part_time", "flexible"];

const EMPTY = {
  first_name: "", last_name: "", email: "", phone: "",
  city: "", province_state: "", country: "Canada",
  age_confirmation_18_plus: false,
  interest_tracks: [], sales_experience_level: "", strengths: [],
  languages: [], availability: "", candidate_goal: "",
  communication_consent: false,
  phone_contact_consent: false,
  ai_call_consent: false,
  do_not_call_request: false,
};

const STEP_REQUIRED = {
  0: ["first_name", "last_name", "email", "phone", "city", "province_state", "country"],
};

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-full border px-4 py-2 text-left text-sm transition-colors duration-200 ${
        active
          ? "border-accent bg-accent text-accent-foreground"
          : "border-border bg-surface text-foreground hover:border-accent"
      }`}
    >
      {children}
    </button>
  );
}

export default function Join() {
  const { t, lang } = useI18n();
  const { attribution, sessionId, track } = useFunnel(lang);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resume, setResume] = useState(null);
  const [done, setDone] = useState(false);
  const formRef = useRef(null);
  const copy = t("join");

  useEffect(() => {
    if (sessionId) track("landing_view");
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
    track("form_started");
  };

  const toggle = (key, value) => {
    setForm((prev) => {
      const list = prev[key];
      return { ...prev, [key]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value] };
    });
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
    track("form_started");
  };

  const validateField = (key) => {
    const value = String(form[key] ?? "").trim();
    if (!value) return copy.errors.required;
    if (key === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) return copy.errors.email;
    if (key === "phone" && value.replace(/\D/g, "").length < 10) return copy.errors.phone;
    return "";
  };

  const validateStep = (index) => {
    const errors = {};
    (STEP_REQUIRED[index] || []).forEach((key) => {
      const message = validateField(key);
      if (message) errors[key] = message;
    });
    if (index === 0 && !form.age_confirmation_18_plus) errors.age_confirmation_18_plus = copy.errors.age;
    if (index === 1 && form.interest_tracks.length === 0) errors.interest_tracks = copy.errors.tracks;
    setFieldErrors(errors);
    if (Object.keys(errors).length) {
      setError(copy.errors.step);
      toast.error(copy.errors.step);
      return false;
    }
    setError("");
    return true;
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    track("form_step_completed", { step: step + 1 });
    setStep((s) => Math.min(2, s + 1));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!validateStep(0)) { setStep(0); return; }
    if (!validateStep(1)) { setStep(1); return; }
    if (!form.communication_consent) {
      setFieldErrors({ communication_consent: copy.errors.consent });
      setError(copy.errors.consent);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`${API}/applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          languages: form.languages,
          sales_experience_level: form.sales_experience_level || null,
          availability: form.availability || null,
          candidate_goal: form.candidate_goal || null,
          preferred_language: lang,
          attribution,
          session_id: sessionId,
          website: "",
        }),
      });
      if (!response.ok) throw new Error("submit");
      const data = await response.json();
      track("form_step_completed", { step: 3 });

      if (resume) {
        const body = new FormData();
        body.append("file", resume);
        const upload = await fetch(`${API}/applications/${data.application_id}/resume`, {
          method: "POST",
          headers: { "X-Upload-Token": data.upload_token },
          body,
        });
        if (!upload.ok) toast.error(copy.errors.resume);
      }
      fetch(`${API}/applications/${data.application_id}/finalize`, {
        method: "POST",
        headers: { "X-Upload-Token": data.upload_token },
      }).catch(() => {});
      setDone(true);
      toast.dismiss();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setError(copy.errors.server);
      toast.error(copy.errors.server);
    } finally {
      setBusy(false);
    }
  };

  const fieldError = (key) =>
    fieldErrors[key] ? (
      <p data-testid={`join-error-${key.replace(/_/g, "-")}`} role="alert" className="mt-2 text-xs text-destructive">
        {fieldErrors[key]}
      </p>
    ) : null;

  const text = (key) => (
    <div>
      <Label htmlFor={key}>{copy.identity[key]} *</Label>
      <Input
        id={key}
        data-testid={`join-${key.replace(/_/g, "-")}`}
        value={form[key]}
        aria-invalid={Boolean(fieldErrors[key])}
        onChange={(event) => set(key, event.target.value)}
        className={fieldErrors[key] ? "mt-2 border-destructive" : "mt-2"}
      />
      {fieldError(key)}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Seo pageKey="apply" />

      <header className="border-b border-border bg-surface">
        <div className="cs-container flex items-center justify-between py-4">
          <Link to={`/${lang}`} data-testid="join-brand" className="font-display text-base tracking-wide">
            CONSULTANTS SPARKS
          </Link>
          <a
            href={PHONE_HREF}
            data-testid="join-header-phone"
            aria-label={t("chat.callAria")}
            className="flex items-center gap-2 text-sm text-foreground hover:text-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <Phone className="h-4 w-4" />
            <span className="hidden sm:inline">{PHONE_LABEL}</span>
          </a>
        </div>
      </header>

      {done ? (
        <section data-testid="join-success" className="cs-container py-20 sm:py-28">
          <div className="max-w-2xl">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground">
              <Check className="h-6 w-6" />
            </span>
            <h1 className="mt-8 font-display text-3xl font-light leading-tight sm:text-4xl">
              {copy.successTitle}
            </h1>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground">{copy.successBody}</p>
            <p className="mt-8 text-sm text-muted-foreground">{copy.successPhone}</p>
            <a
              href={PHONE_HREF}
              data-testid="join-success-phone"
              className="mt-2 inline-flex items-center gap-2 text-lg text-accent hover:underline"
            >
              <Phone className="h-4 w-4" /> {PHONE_LABEL}
            </a>
            <div className="mt-10">
              <Button asChild variant="outline" className="rounded-full" data-testid="join-back-home">
                <Link to={`/${lang}`}>{copy.backHome}</Link>
              </Button>
            </div>
          </div>
        </section>
      ) : (
        <>
          <section className="cs-container py-14 sm:py-20">
            <p className="cs-kicker text-accent">{copy.kicker}</p>
            <h1 className="mt-5 max-w-3xl font-display text-3xl font-light leading-tight sm:text-5xl lg:text-6xl">
              {copy.title}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground">{copy.lead}</p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Button
                data-testid="join-hero-cta"
                onClick={() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="rounded-full bg-accent px-7 py-6 text-accent-foreground hover:bg-accent/90 sm:py-3"
              >
                {copy.cta} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button asChild variant="outline" className="rounded-full px-7 py-6 sm:py-3" data-testid="join-hero-phone">
                <a href={PHONE_HREF}>{copy.ctaSecondary}</a>
              </Button>
            </div>
          </section>

          <section className="border-y border-border bg-surface py-14">
            <div className="cs-container">
              <h2 className="text-base font-medium md:text-lg">{copy.valueTitle}</h2>
              <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2">
                {copy.values.map((value) => (
                  <div key={value.title} data-testid="join-value">
                    <p className="text-sm font-medium text-accent">{value.title}</p>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{value.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="cs-container py-14">
            <h2 className="text-base font-medium md:text-lg">{copy.processTitle}</h2>
            <ol className="mt-6 space-y-4">
              {copy.process.map((line, index) => (
                <li key={line} className="flex gap-4 text-sm leading-relaxed text-muted-foreground">
                  <span className="cs-kicker text-accent">{`0${index + 1}`}</span>
                  {line}
                </li>
              ))}
            </ol>
            <p data-testid="join-honesty" className="mt-8 max-w-2xl text-xs leading-relaxed text-muted-foreground">
              {copy.honesty}
            </p>
          </section>

          <section ref={formRef} className="border-t border-border bg-surface py-14 sm:py-20">
            <div className="cs-container">
              <h2 className="text-base font-medium md:text-lg">{copy.formTitle}</h2>
              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
                {copy.steps.map((label, index) => (
                  <span
                    key={label}
                    data-testid={`join-step-label-${index}`}
                    className={`cs-kicker ${index === step ? "text-accent" : "text-muted-foreground/60"}`}
                  >
                    {label}
                  </span>
                ))}
              </div>

              <form onSubmit={submit} className="mt-8 max-w-2xl space-y-8" noValidate>
                {step === 0 && (
                  <div data-testid="join-step-0" className="space-y-5">
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      {text("first_name")}
                      {text("last_name")}
                      {text("email")}
                      {text("phone")}
                      {text("city")}
                      {text("province_state")}
                    </div>
                    {text("country")}
                    <label className="flex items-start gap-3 text-sm">
                      <Checkbox
                        data-testid="join-age"
                        checked={form.age_confirmation_18_plus}
                        onCheckedChange={(value) => set("age_confirmation_18_plus", Boolean(value))}
                        className="mt-0.5"
                      />
                      <span>{copy.identity.age} *</span>
                    </label>
                    {fieldError("age_confirmation_18_plus")}
                  </div>
                )}

                {step === 1 && (
                  <div data-testid="join-step-1" className="space-y-4">
                    <div>
                      <p className="text-sm font-medium">{copy.interestsTitle} *</p>
                      <p className="mt-1 text-xs text-muted-foreground">{copy.interestsHint}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(copy.tracks).map(([key, label]) => (
                        <Chip
                          key={key}
                          testid={`join-track-${key}`}
                          active={form.interest_tracks.includes(key)}
                          onClick={() => toggle("interest_tracks", key)}
                        >
                          {label}
                        </Chip>
                      ))}
                    </div>
                    {fieldError("interest_tracks")}
                  </div>
                )}

                {step === 2 && (
                  <div data-testid="join-step-2" className="space-y-8">
                    <div>
                      <p className="text-sm font-medium">{copy.profile.salesTitle}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {SALES_LEVELS.map((level) => (
                          <Chip
                            key={level}
                            testid={`join-sales-${level}`}
                            active={form.sales_experience_level === level}
                            onClick={() => set("sales_experience_level", form.sales_experience_level === level ? "" : level)}
                          >
                            {copy.profile.sales[level]}
                          </Chip>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium">{copy.profile.strengthsTitle}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {Object.entries(copy.profile.strengths).map(([key, label]) => (
                          <Chip
                            key={key}
                            testid={`join-strength-${key}`}
                            active={form.strengths.includes(key)}
                            onClick={() => toggle("strengths", key)}
                          >
                            {label}
                          </Chip>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="languages">{copy.profile.languagesTitle}</Label>
                      <Input
                        id="languages"
                        data-testid="join-languages"
                        placeholder={copy.profile.languagesHint}
                        value={form.languages.join(", ")}
                        onChange={(event) =>
                          set("languages", event.target.value.split(",").map((v) => v.trim()).filter(Boolean))
                        }
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <p className="text-sm font-medium">{copy.profile.availabilityTitle}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {AVAILABILITY.map((value) => (
                          <Chip
                            key={value}
                            testid={`join-availability-${value}`}
                            active={form.availability === value}
                            onClick={() => set("availability", form.availability === value ? "" : value)}
                          >
                            {copy.profile.availability[value]}
                          </Chip>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="candidate_goal">{copy.profile.goalTitle}</Label>
                      <Textarea
                        id="candidate_goal"
                        data-testid="join-goal"
                        rows={4}
                        maxLength={2000}
                        placeholder={copy.profile.goalPlaceholder}
                        value={form.candidate_goal}
                        onChange={(event) => set("candidate_goal", event.target.value)}
                        className="mt-2"
                      />
                    </div>

                    <div className="border border-border bg-background p-5">
                      <p className="text-sm font-medium">{copy.resumeTitle}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{copy.resumeHint}</p>
                      <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-full border border-border px-4 py-2 text-sm transition-colors hover:border-accent">
                        <Upload className="h-4 w-4" />
                        {resume ? resume.name : copy.resumeChoose}
                        <input
                          type="file"
                          data-testid="join-resume"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          className="hidden"
                          onChange={(event) => setResume(event.target.files?.[0] || null)}
                        />
                      </label>
                    </div>

                    <div className="border border-border bg-background p-5">
                      <p className="text-sm font-medium">{copy.consentTitle} *</p>
                      <label className="mt-4 flex items-start gap-3 text-xs leading-relaxed">
                        <Checkbox
                          data-testid="join-consent"
                          checked={form.communication_consent}
                          onCheckedChange={(value) => set("communication_consent", Boolean(value))}
                          className="mt-0.5"
                        />
                        <span>{copy.consent}</span>
                      </label>
                      {fieldError("communication_consent")}

                      <div
                        data-testid="join-lea-section"
                        className="mt-6 border-2 border-accent bg-accent/5 p-5"
                      >
                        <p className="text-base font-medium md:text-lg">{copy.consentLeaTitle}</p>
                        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                          {copy.consentLeaHint}
                        </p>
                        {form.do_not_call_request && (
                          <p
                            data-testid="join-lea-dnc-active"
                            className="mt-3 text-xs font-medium text-destructive"
                          >
                            {copy.consentDncActive}
                          </p>
                        )}
                        <label className="mt-4 flex items-start gap-3 text-xs leading-relaxed">
                          <Checkbox
                            data-testid="join-consent-phone"
                            checked={form.phone_contact_consent}
                            onCheckedChange={(value) => set("phone_contact_consent", Boolean(value))}
                            className="mt-0.5"
                            disabled={form.do_not_call_request}
                          />
                          <span>{copy.consentPhone}</span>
                        </label>
                        <label className="mt-4 flex items-start gap-3 text-xs leading-relaxed">
                          <Checkbox
                            data-testid="join-consent-ai-call"
                            checked={form.ai_call_consent}
                            onCheckedChange={(value) => set("ai_call_consent", Boolean(value))}
                            className="mt-0.5"
                            disabled={form.do_not_call_request}
                          />
                          <span>{copy.consentAi}</span>
                        </label>
                        {!form.do_not_call_request && (
                          <p
                            data-testid={form.phone_contact_consent && form.ai_call_consent
                              ? "join-lea-ready" : "join-lea-incomplete"}
                            className="mt-4 text-xs font-medium"
                          >
                            {form.phone_contact_consent && form.ai_call_consent
                              ? copy.consentLeaReady : copy.consentLeaIncomplete}
                          </p>
                        )}
                      </div>

                      <div className="mt-6 border-t border-border pt-4">
                        <p className="text-sm font-medium">{copy.consentOptionalTitle}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{copy.consentOptionalHint}</p>
                        <label className="mt-4 flex items-start gap-3 text-xs leading-relaxed">
                          <Checkbox
                            data-testid="join-do-not-call"
                            checked={form.do_not_call_request}
                            onCheckedChange={(value) => {
                              const next = Boolean(value);
                              set("do_not_call_request", next);
                              if (next) {
                                set("phone_contact_consent", false);
                                set("ai_call_consent", false);
                              }
                            }}
                            className="mt-0.5"
                          />
                          <span>{copy.consentDnc}</span>
                        </label>
                        <p data-testid="join-dnc-hint"
                           className="mt-2 pl-9 text-xs leading-relaxed text-muted-foreground">
                          {copy.consentDncHint}
                        </p>
                      </div>
                      <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
                        <Link to={`/${lang}${routeSlug("privacy", lang)}`} className="underline" data-testid="join-privacy-link">
                          {t("footer.privacy")}
                        </Link>
                      </p>
                    </div>
                  </div>
                )}

                {error && (
                  <p data-testid="join-error" role="alert" className="text-sm text-destructive">
                    {error}
                  </p>
                )}

                <div className="flex flex-col gap-3 border-t border-border pt-6 sm:flex-row sm:justify-between">
                  <Button
                    type="button"
                    variant="ghost"
                    data-testid="join-back"
                    disabled={step === 0}
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    className="rounded-full"
                  >
                    {copy.back}
                  </Button>
                  {step < 2 ? (
                    <Button
                      type="button"
                      data-testid="join-next"
                      onClick={goNext}
                      className="rounded-full bg-accent px-8 py-6 text-accent-foreground hover:bg-accent/90 sm:py-3"
                    >
                      {copy.next} <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      data-testid="join-submit"
                      disabled={busy}
                      className="rounded-full bg-accent px-8 py-6 text-accent-foreground hover:bg-accent/90 sm:py-3"
                    >
                      {busy ? copy.submitting : copy.submit}
                    </Button>
                  )}
                </div>
              </form>
            </div>
          </section>

          <footer className="cs-container py-10">
            <p className="text-xs text-muted-foreground/70">
              © {new Date().getFullYear()} Consultants Sparks ·{" "}
              <Link to={`/${lang}${routeSlug("privacy", lang)}`} className="underline">
                {t("footer.privacy")}
              </Link>{" "}
              ·{" "}
              <Link to={`/${lang}${routeSlug("terms", lang)}`} className="underline">
                {t("footer.terms")}
              </Link>
            </p>
          </footer>
        </>
      )}

      <AskVytali />
      <MobileQuickActions />
    </div>
  );
}
